import { useEffect, useState } from 'react';

import { Button } from '@mui/material';
import { Loader2 } from 'lucide-react';

import { MasterIndexApi, MasterIndexApiPath } from '@/api';
import { MasterNameApi } from '@/api/api.names.master';
import { WizardModal } from '@/components';
import { LocalStorageItems } from '@/constants';
import { withLoader, WithLoaderProps } from '@/hocs';
import { IWantedPerson } from '@/models/wanted.person';
import { getSasTokenFromFileUrl } from '@/services';
import {
  ConfirmTypes,
  useAgencyStore,
  useToastStore,
  useUserStore,
} from '@/store';
import { getFileType, getFileUrlWithValidToken } from '@/utils';

import { buildPersonReportDefinition } from './buildPersonReportDefinition';

interface PersonPrintModalProps extends WithLoaderProps {
  personId: string | null;
  onClose: () => void;
}

const PersonPrintModal = ({
  personId,
  onClose,
  showLoader,
  hideLoader,
}: PersonPrintModalProps) => {
  const open = !!personId;

  const { user } = useUserStore();
  const { currentAgency, fetchAgency } = useAgencyStore();
  const { updateToast } = useToastStore();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!personId) return;

    let cancelled = false;
    setPdfUrl(null);
    showLoader();

    (async () => {
      try {
        const [personRes, notesRes, relatedRes, wantedRes] = await Promise.all([
          MasterIndexApi.getMasterPerson(personId),
          MasterNameApi.getPaginatedMasterNameNotes(personId, { limit: 1000 }),
          MasterIndexApi.getMasterRelatedRecords(
            MasterIndexApiPath.People,
            personId,
            { limit: 1000, sort: JSON.stringify({ createdAt: -1 }) },
          ),
          MasterIndexApi.getMasterWantedRecords(
            MasterIndexApiPath.People,
            personId,
            { limit: 1000 },
          ),
        ]);

        let agencyData = currentAgency;
        if (!agencyData) {
          const agencyId = localStorage.getItem(
            LocalStorageItems.CurrentAgencyId,
          );
          if (agencyId) agencyData = (await fetchAgency(agencyId)) ?? null;
        }

        const person = personRes.data;

        // Fetch full SSN (the main endpoint returns masked SSN)
        let fullSsn: string | undefined;
        try {
          const ssnRes = await MasterNameApi.getMasterNameSSN(personId);
          fullSsn = ssnRes.data?.ssn;
        } catch {
          // SSN fetch failed — use whatever is in person.ssn
        }

        // Resolve photo URLs with SAS tokens for Azure Blob Storage
        const photoUrls: string[] = [];
        const allFiles = person.files ?? [];
        const filesWithUrl = allFiles.filter((f) => f.url);
        if (filesWithUrl.length > 0) {
          const sasToken = await getSasTokenFromFileUrl(filesWithUrl[0].url);
          for (const file of filesWithUrl) {
            if (file.url && getFileType(file.mimeType) === 'image') {
              const validUrl = getFileUrlWithValidToken(file, sasToken);
              photoUrls.push(validUrl);
            }
          }
        }

        // Collect non-image document files
        const documentFiles = allFiles
          .filter((f) => getFileType(f.mimeType) !== 'image')
          .map((f) => ({ name: f.name, size: f.size, mimeType: f.mimeType }));

        const definition = await buildPersonReportDefinition({
          person: fullSsn ? { ...person, ssn: fullSsn } : person,
          notes: notesRes.data?.docs ?? [],
          relatedRecords: (relatedRes.data?.docs ?? []).flat(),
          wantedRecords: (wantedRes.data?.results ??
            []) as unknown as IWantedPerson[],
          agency: agencyData,
          user,
          printedAt: new Date(),
          photoUrls,
          documentFiles,
        });

        const pdfMakeModule = await import('pdfmake/build/pdfmake');
        const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
        const pdfMakeInstance = (pdfMakeModule as any).default || pdfMakeModule;
        const fontsInstance = (pdfFontsModule as any).default || pdfFontsModule;
        if (pdfMakeInstance && fontsInstance) {
          pdfMakeInstance.vfs = fontsInstance.pdfMake?.vfs || fontsInstance;
        }

        const createPdf = pdfMakeInstance.createPdf || pdfMakeInstance;
        createPdf(definition).getBlob((blob: Blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
          hideLoader();
        });
      } catch (err: any) {
        if (cancelled) return;
        hideLoader();
        const message =
          'Try Again: ' +
          (typeof err === 'string'
            ? err
            : err?.response?.data?.message ||
              err?.message ||
              'Failed to generate print');
        updateToast({ open: true, message, type: ConfirmTypes.ERROR });
        onClose();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [personId]);

  const handleClose = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    onClose();
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      const iframe = document.createElement('iframe');
      iframe.src = pdfUrl;
      iframe.width = '100%';
      iframe.height = '100%';
      iframe.style.border = 'none';
      iframe.onload = function () {
        setTimeout(function () {
          iframe.focus();
          iframe?.contentWindow?.print();
        }, 1000);
      };
      newWindow.document.body.style.margin = '0';
      newWindow.document.body.appendChild(iframe);
    }
  };

  return (
    <WizardModal open={open} onClose={handleClose} width="70%" minWidth="210mm">
      <div className="h-full flex flex-col bg-white dark:bg-[#0B1922] text-gray-900 dark:text-gray-100 rounded-md">
        <div className="px-4 pt-4 pb-0" style={{ flex: 1, minHeight: 0 }}>
          {!pdfUrl && (
            <div
              className="flex-1 flex items-center justify-center border border-gray-300 dark:border-gray-600 rounded-lg"
              style={{ height: '100%', backgroundColor: '#2B2B28' }}
            >
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                <p className="text-sm text-gray-400">
                  Generating PDF preview...
                </p>
              </div>
            </div>
          )}
          {pdfUrl && (
            <div
              className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden"
              style={{ height: '100%' }}
            >
              <embed
                src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                type="application/pdf"
                width="100%"
                height="100%"
                style={{ border: 'none', display: 'block' }}
              />
            </div>
          )}
        </div>

        <div className="flex justify-between items-center px-4 py-4 border-t border-gray-200 dark:border-[#192932] bg-white dark:bg-[#17252D] rounded-b-md">
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handlePrint}
            disabled={!pdfUrl}
          >
            Print
          </Button>
        </div>
      </div>
    </WizardModal>
  );
};

export default withLoader(PersonPrintModal);
